import React from "react";
import { AlertTriangle, CalendarRange, Check, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/format";
import { PLACES, limitsText } from "@/lib/veiviser";
import { Field, fieldCls } from "@/components/veiviser/WizardChrome";
import { DateField } from "@/components/veiviser/DateField";

const TONE = { ok: "v-ok", tiltak: "v-assumed", konflikt: "v-blocked", blokkert: "v-blocked" };

function Places({ v, set, catalog, preview, template }) {
  const sug = preview?.suggested_place;
  const limits = Object.fromEntries((catalog?.places || []).map((p) => [p.id, p.limits]));
  return (
    <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" data-testid="vv-places">
      {PLACES.map((p) => {
        const fits = p.key === "kai" || !sug?.fits || sug.fits[p.key] !== false;
        const lockedToKai = template?.kai_only && p.key !== "kai";
        const disabled = !fits || lockedToKai;
        const on = v.period.place === p.key;
        return (
          <button key={p.key} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => set("period.place", p.key)} className={cn("rounded-[16px] border p-3.5 text-left transition-[border-color,box-shadow] duration-150", on ? "border-[hsl(var(--navy))] shadow-[0_12px_26px_-20px_hsl(var(--navy)/0.8)]" : "border-black/[0.08] hover:border-black/[0.2]", disabled && "cursor-not-allowed opacity-45 hover:border-black/[0.08]")} data-testid={`vv-place-${p.key}`} data-selected={on ? "1" : "0"}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[14.5px] font-semibold">{p.label}</span>
              {on ? <Check className="h-4 w-4 text-[hsl(var(--navy))]" strokeWidth={2.8} /> : sug?.place === p.key ? <span className="v-badge v-ok"><Sparkles className="h-3 w-3" /> Foreslått</span> : null}
            </div>
            <div className="mt-1 text-[12px] text-muted-foreground">{lockedToKai ? "Arbeidet gjøres ved kai" : !fits ? "For stort for fartøyet" : p.key === "kai" ? "Arbeid ved verftskai" : limitsText(limits[p.key])}</div>
          </button>
        );
      })}
    </div>
  );
}

function Capacity({ cap }) {
  if (!cap) return <p className="flex items-center gap-2 text-[13px] text-muted-foreground" data-testid="vv-cap-empty"><CalendarRange className="h-4 w-4" /> Velg oppstart, så sjekker jeg dokk, kai og fagene mot planen.</p>;
  return (
    <div className="rounded-[16px] border border-black/[0.07] p-4" data-testid="vv-cap" data-state={cap.state}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn("v-badge", TONE[cap.state] || "v-assumed")} data-testid="vv-cap-label">{cap.state === "ok" ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{cap.label}</span>
        <span className="text-[12.5px] text-muted-foreground">{fmtDate(cap.fra)}–{fmtDate(cap.til, { year: true })} · {cap.days} døgn{cap.uker ? ` · ${cap.uker}` : ""} · {Math.round(cap.timer)} timer</span>
      </div>
      {(cap.ressurser || []).map((r) => (
        <div key={r.id} className="mt-3 text-[13px]" data-testid={`vv-cap-res-${r.id}`}>
          <span className="font-medium">{r.name}</span>{" "}
          {r.ok ? <span className="text-[hsl(var(--v-ok))]">er ledig i perioden</span> : <span className="text-[hsl(var(--v-blocked))]">er opptatt: {r.konflikter.map((k) => `${k.navn} (${fmtDate(k.fra)}–${fmtDate(k.til)})`).join(", ")}</span>}
        </div>
      ))}
      {cap.worst?.length ? (
        <div className="mt-3 space-y-1.5">
          {cap.worst.map((w) => (
            <div key={`${w.avdeling}-${w.uke}`} className="flex items-center gap-3 text-[12.5px]" data-testid={`vv-cap-dept-${w.avdeling}`}>
              <span className="w-24 shrink-0 truncate text-foreground/80">{w.name}</span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]"><span className="block h-full rounded-full" style={{ width: `${Math.min(100, w.pct)}%`, background: w.pct > 100 ? "hsl(var(--v-blocked))" : w.color }} /></span>
              <span className="w-24 shrink-0 text-right font-mono tabular-nums text-muted-foreground">{w.pct} % · {w.uke}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function StepPeriode({ v, set, catalog, preview, template, cap }) {
  return (
    <div className="space-y-9" data-testid="vv-periode">
      <section>
        <div className="mb-3 text-[13px] font-semibold">Hvor</div>
        <Places v={v} set={set} catalog={catalog} preview={preview} template={template} />
        {preview?.suggested_place?.why ? <p className="mt-2 text-[12.5px] text-muted-foreground" data-testid="vv-place-why">{preview.suggested_place.why}.</p> : null}
      </section>
      <section>
        <div className="mb-3 text-[13px] font-semibold">Når</div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Oppstart (valgfritt)"><DateField value={v.period.from} onChange={(x) => set("period.from", x)} placeholder="Velg oppstart" testId="vv-period-from" future /></Field>
          <Field label="Varighet (døgn)"><input inputMode="numeric" value={v.period.days} onChange={(e) => set("period.days", e.target.value.replace(/\D/g, ""))} placeholder={String(template?.days || 7)} className={fieldCls} data-testid="vv-period-days" /></Field>
        </div>
        <div className="mt-4"><Capacity cap={cap} /></div>
      </section>
      <section>
        <div className="mb-3 text-[13px] font-semibold">Frister</div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tilbudsfrist"><DateField value={v.facts.tender_deadline} onChange={(x) => set("facts.tender_deadline", x)} testId="vv-tender-deadline" future /></Field>
          <Field label="Forventet tildeling"><DateField value={v.facts.award_expected} onChange={(x) => set("facts.award_expected", x)} testId="vv-award-expected" future /></Field>
        </div>
      </section>
    </div>
  );
}
